"use client"

import clsx from "clsx"
import React, { useEffect, useState } from "react"

import FormFieldBase from "./FormFieldBase"
import { useFormErrorContext } from "./StepWrapper"

import type { ChangeEvent, FocusEvent } from "react"

export interface WebNativeSelectInputProps {
  label: string
  options: Array<{ value: string; label: string }>
  value: string
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void
  onBlur?: (e: FocusEvent<HTMLSelectElement>) => void
  onFocus?: (e: FocusEvent<HTMLSelectElement>) => void
  isValid?: boolean
  touched?: boolean
  blurred?: boolean
  errorMessage?: string | null
  className?: string
}

const SelectInput: React.FC<WebNativeSelectInputProps> = ({
  label,
  options,
  value,
  onChange,
  onBlur,
  onFocus,
  isValid = false,
  touched = false,
  blurred = false,
  errorMessage = null,
  className = "",
}) => {
  const clearFormError = useFormErrorContext()
  const [hasValue, setHasValue] = useState(value !== "")
  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (clearFormError) {
      clearFormError()
    }
    onChange(e)
  }

  const showError = !isValid && touched && blurred && hasValue && errorMessage?.trim()

  useEffect(() => {
    setHasValue(value !== "")
  }, [value])

  return (
    <FormFieldBase errorMessage={hasValue ? errorMessage : null} isValid={isValid} touched={touched} blurred={blurred} hasValue={hasValue}>
      <div className="relative">
        <div className="flex flex-col">
          {hasValue && label && (
            <div className={clsx("absolute -top-1 left-0 text-xs font-bold", showError ? "text-[#C20000]" : "text-gray-700")}>{label}</div>
          )}
          <select
            className={clsx(
              "relative w-full appearance-none border-0 bg-transparent pl-0 pr-10 text-base transition-all duration-200 focus:outline-none",
              hasValue ? "bottom-[-2px] pb-0 pt-3 text-gray-900" : "py-1.5 text-[#444444]",
              className
            )}
            value={value}
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
          >
            <option value="" disabled>
              {!hasValue ? label : ""}
            </option>
            {options.map((option) => (
              <option key={option.value} value={option.value} className="text-gray-900">
                {option.label}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
            {showError && (
              <div className="flex h-6 w-6 items-center justify-center rounded-full border-[1.5px] border-[#C20000] text-[#C20000]">
                <span className="font-bold">!</span>
              </div>
            )}
            {isValid && !showError && hasValue && (
              <div className="text-blue-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            {!isValid && !showError && (
              <svg
                className="h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </div>
        </div>
      </div>
    </FormFieldBase>
  )
}

export default SelectInput
